import { createThunkRequest, GET, POST } from '@webapp/account/src/libs/request';
import { mapPage } from '@webapp/account/src/resources/common';
import { reverseMapCondition } from '@webapp/account/src/resources/logic/map-logic';
import { mapQuestion } from '@webapp/account/src/resources/questions/map';

import type { LogicCondition } from '@webapp/common/entities/logic';
import {
    LogicAction,
    LogicTransition,
    LogicTransitionType,
    LogicType,
    QuestionType,
} from '@webapp/common/resources/survey';

import type { LogicPageQuestion } from '../logic/question';

import { mapCondition, mapLink } from './map';
import { getConditionById } from './selectors';

export const getLinkParamsWithoutValues = createThunkRequest(
    'logic-link-params/getLinkParamsWithoutValues',
    async (surveyId: number) => {
        const { list } = await POST('list/link_params', {
            survey: surveyId,
        });
        return list.map(mapLink);
    },
    { notify: false },
);

export const loadLogicPagesAndQuestionsData = createThunkRequest(
    'logic-link-params/loadPagesAndQuestionsData',
    async ({ surveyId }: { surveyId: number }) => {
        const { pages, questions } = await POST('list/page_question', {
            survey: surveyId,
        });

        let questionsMapped: Array<LogicPageQuestion> = questions.map((item) => mapQuestion(item));
        questionsMapped = questionsMapped.map((quest) => {
            if (quest.type === QuestionType.TEXT_BLOCK) {
                return {
                    ...quest,
                    params: {
                        ...quest.params,
                        textBlock:
                            typeof quest.params.textBlock !== 'string'
                                ? quest.params.textBlock
                                : quest.params.textBlock.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                      const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                      if (!q) return '';
                                      return `{Q${q.order}}`;
                                  }),
                    },
                };
            }
            return {
                ...quest,
                name: quest.name.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                    const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                    if (!q) return '';
                    return `{Q${q.order}}`;
                }),
                cols: quest.cols.map((col) => {
                    return {
                        ...col,
                        name:
                            typeof col.name !== 'string'
                                ? col.name
                                : col.name.toString().replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                      const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                      if (!q) return '';
                                      return `{Q${q.order}}`;
                                  }),
                    };
                }),
                rows: quest.rows.map((row) => {
                    return {
                        ...row,
                        value:
                            typeof row.value !== 'string'
                                ? row.value
                                : row.value.toString().replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                      const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                      if (!q) return '';
                                      return `{Q${q.order}}`;
                                  }),
                    };
                }),
                conditions: quest.conditions.map((cond) => ({
                    ...cond,
                    completeText:
                        typeof cond.completeText !== 'string'
                            ? cond.completeText
                            : cond.completeText.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                  const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                  if (!q) return '';
                                  return `{Q${q.order}}`;
                              }),
                    disqualText:
                        typeof cond.disqualText !== 'string'
                            ? cond.disqualText
                            : cond.disqualText.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                  const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                                  if (!q) return '';
                                  return `{Q${q.order}}`;
                              }),
                })),
            };
        });
        return {
            pages: pages.map(mapPage),
            questions: questionsMapped,
        };
    },
    { notify: false },
);

export const loadLinkParamsLogic = createThunkRequest(
    'logic-link-params/loadLinkParamsLogic',
    async ({ surveyId }: { surveyId: number }) => {
        const { logics } = await GET('get/survey_logics', {
            surveyId,
            logicType: [LogicType.LINK_PARAM],
        });

        return {
            logics: logics.map(mapCondition),
        };
    },
    { notify: false },
);

const initialCondition: Partial<LogicCondition> = {
    respondent: LogicType.LINK_PARAM,
    action: LogicAction.SHOW,
    transition: LogicTransition.QUESTIONS,
    toQuestions: [],
    toPages: [],
    transitionType: LogicTransitionType.STANDART,
};

export const addLogicLinkParamsCondition = createThunkRequest(
    'logic-link-params/addLogicLinkParamsCondition',
    async ({ linkId, surveyId }: { linkId: number; surveyId: number }) => {
        const { id, message } = await POST('add/logic', {
            surveyId: surveyId,
            linkParamId: linkId,
            params: JSON.stringify({ linkParamValue: '' }),
            ...reverseMapCondition({
                ...initialCondition,
            }),
        });

        return {
            id: Number(id),
            linkParamId: linkId,
            linkParamValue: '',
            ...initialCondition,
            message,
        };
    },
);

export const removeLogicLinkParamsCondition = createThunkRequest(
    'logic-link-params/removeLogicLinkParamsCondition',
    async ({ conditionId, linkId }: { conditionId: number; linkId: number }) => {
        const { message } = await POST('delete/logic', {
            id: conditionId,
        });

        return { conditionId, linkId, message };
    },
);

export const changeLogicLinkParamsConditionParam = createThunkRequest(
    'logic-link-params/changeLogicLinkParamsConditionParam',
    async (
        {
            conditionId,
            params,
        }: {
            conditionId: number;
            params: Partial<LogicCondition>;
        },
        { getState },
    ) => {
        const state = getState();
        const cond = getConditionById(state, conditionId);
        const payload = {
            logicId: conditionId,
            ...reverseMapCondition(params),
        };
        if (params.linkParamId || params.linkParamValue) {
            payload.params = JSON.stringify({
                // linkParamId: params.linkParamId || cond.linkParamId,
                linkParamValue: params.linkParamValue || cond.linkParamValue,
            });
        }
        const { message } = await POST('change/logic', payload);

        return {
            message,
            conditionId,
            params,
        };
    },
);
